import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Users, Building2, Briefcase, ClipboardList, MessageSquare, CheckCircle2, IndianRupee, Wallet, AlertOctagon, Star, MapPin, Download, RefreshCw, TrendingUp } from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { DashboardShell } from "../../components/shared/DashboardShell";
import { StatCard } from "../../components/shared/StatCard";
import { adminNav } from "../../components/shared/navConfig";
import api from "../../lib/api";
import { useTheme } from "../../context/ThemeContext";

const fmt = (n) => (typeof n === "number" ? n.toLocaleString("en-IN") : n ?? "—");
const inr = (n) => `₹${fmt(Math.round(Number(n) || 0))}`;

const RANGES = [
  { key: 7,  label: "7d"  },
  { key: 30, label: "30d" },
  { key: 90, label: "90d" },
];

const STATUS_COLORS = {
  Approved: "#3B82F6", Pending: "#F59E0B", Allocated: "#8B5CF6",
  "In Progress": "#06B6D4", "Check-in Open": "#0EA5E9",
  Submitted: "#EAB308", "Revision Required": "#F97316",
  Completed: "#10B981", Rejected: "#F43F5E", Draft: "#64748B",
};

export default function AdminAnalytics() {
  const { theme } = useTheme();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (d = days) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/analytics", { params: { days: d } });
      setData(data);
    } catch (e) {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(days); /* eslint-disable-next-line */ }, [days]);

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/analytics/export.csv", { params: { days }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `assisto-analytics-${days}d.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const totals = data?.totals || {};
  const trend  = data?.trend || [];
  const cities = data?.cities || [];
  const statusSplit = data?.status_split || [];

  const axisColor = theme === "light" ? "#64748B" : "#98A2B3";
  const gridColor = theme === "light" ? "rgba(15,23,42,0.06)" : "rgba(255,255,255,0.06)";

  const maxCityActivity = useMemo(
    () => Math.max(1, ...cities.map(c => c.tasks + c.students + c.companies)),
    [cities],
  );

  const headerRight = (
    <div className="flex items-center gap-2">
      <div className="inline-flex rounded-md border overflow-hidden" style={{ borderColor: "var(--border-strong)" }}>
        {RANGES.map(r => (
          <button
            key={r.key}
            data-testid={`analytics-range-${r.key}`}
            onClick={() => setDays(r.key)}
            className="px-3 py-1.5 text-xs transition-colors"
            style={{
              background: days === r.key ? "var(--surface-2)" : "transparent",
              color: days === r.key ? "var(--text)" : "var(--text-muted)",
              borderRight: r.key !== 90 ? "1px solid var(--border)" : "none",
            }}
          >
            {r.label}
          </button>
        ))}
      </div>
      <button
        data-testid="analytics-refresh-btn"
        onClick={() => load(days)}
        className="btn-ghost px-3 py-1.5 rounded-md text-xs inline-flex items-center gap-1.5"
      >
        <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
      </button>
      <button
        data-testid="analytics-export-csv-btn"
        onClick={exportCsv}
        className="btn-primary px-3 py-1.5 rounded-md text-xs inline-flex items-center gap-1.5"
      >
        <Download size={12}/> Export CSV
      </button>
    </div>
  );

  return (
    <DashboardShell
      nav={adminNav}
      title="Analytics"
      subtitle="Platform pulse — totals, financials, ratings and city-wise activity."
      headerRight={headerRight}
    >
      {/* KPI grid — 12 metrics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
        <StatCard testId="an-students"     label="Students"       value={fmt(totals.students)}      icon={Users}          accent/>
        <StatCard testId="an-companies"    label="Companies"      value={fmt(totals.companies)}     icon={Building2}/>
        <StatCard testId="an-jobs"         label="Jobs posted"    value={fmt(totals.jobs)}          icon={Briefcase}/>
        <StatCard testId="an-applications" label="Applications"   value={fmt(totals.applications)}  icon={ClipboardList}/>
        <StatCard testId="an-allocations"  label="Allocations"    value={fmt(totals.allocations)}   icon={MessageSquare}/>
        <StatCard testId="an-active"       label="Active tasks"   value={fmt(totals.active_tasks)}  icon={Briefcase}      accent/>
        <StatCard testId="an-completed"    label="Completed"      value={fmt(totals.completed_tasks)} icon={CheckCircle2}/>
        <StatCard testId="an-disputes"     label="Disputes open"  value={fmt(totals.disputes_open)} icon={AlertOctagon}/>
        <StatCard testId="an-revenue"      label="Revenue (GMV)"  value={inr(totals.revenue)}       icon={IndianRupee}    accent/>
        <StatCard testId="an-payouts"      label="Payouts"        value={inr(totals.payouts)}       icon={Wallet}/>
        <StatCard testId="an-rating"       label="Avg rating"     value={totals.avg_rating || "0.0"} suffix={totals.ratings_count ? `/5 · ${totals.ratings_count}` : "/5"} icon={Star}/>
        <StatCard testId="an-cities"       label="Active cities"  value={fmt(cities.length)}        icon={MapPin}/>
      </div>

      {/* Trend + Status split */}
      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 card-surface p-5" data-testid="analytics-trend-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="eyebrow">Trend</div>
              <h3 className="font-display text-lg mt-1" style={{ color: "var(--text)" }}>
                Applications & tasks · last {days}d
              </h3>
            </div>
            <span className="text-xs inline-flex items-center gap-1.5" style={{ color: "var(--text-dim)" }}>
              <TrendingUp size={12}/> Live from platform
            </span>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trend} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="gApps" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.35}/>
                    <stop offset="100%" stopColor="#3B82F6" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="gTasks" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10B981" stopOpacity={0.35}/>
                    <stop offset="100%" stopColor="#10B981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
                <XAxis dataKey="date" stroke={axisColor} tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                <YAxis stroke={axisColor} tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    background: "var(--surface-2)",
                    border: "1px solid var(--border-strong)",
                    borderRadius: 8, fontSize: 12, color: "var(--text)",
                  }}
                  labelStyle={{ color: "var(--text-muted)" }}
                />
                <Area type="monotone" dataKey="applications" stroke="#3B82F6" fill="url(#gApps)"  strokeWidth={2}/>
                <Area type="monotone" dataKey="tasks"        stroke="#10B981" fill="url(#gTasks)" strokeWidth={2}/>
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-surface p-5" data-testid="analytics-status-card">
          <div className="eyebrow">Task status split</div>
          <h3 className="font-display text-lg mt-1 mb-2" style={{ color: "var(--text)" }}>Distribution</h3>
          <div className="h-64">
            {statusSplit.length === 0 ? (
              <div className="h-full flex items-center justify-center text-xs" style={{ color: "var(--text-dim)" }}>
                No tasks yet
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statusSplit} dataKey="count" nameKey="status"
                    cx="50%" cy="50%" innerRadius={44} outerRadius={80} paddingAngle={2}
                  >
                    {statusSplit.map((s, i) => (
                      <Cell key={i} fill={STATUS_COLORS[s.status] || "#64748B"} stroke="var(--surface)" strokeWidth={2}/>
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      background: "var(--surface-2)",
                      border: "1px solid var(--border-strong)",
                      borderRadius: 8, fontSize: 12, color: "var(--text)",
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11, color: axisColor }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* City-wise activity */}
      <div className="card-surface p-5" data-testid="analytics-cities-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="eyebrow">City-wise activity</div>
            <h3 className="font-display text-lg mt-1" style={{ color: "var(--text)" }}>
              Where the platform is buzzing
            </h3>
          </div>
          <span className="text-xs" style={{ color: "var(--text-dim)" }}>Top {cities.length}</span>
        </div>
        {cities.length === 0 ? (
          <div className="text-xs py-8 text-center" style={{ color: "var(--text-dim)" }}>
            No city data yet — students/companies will populate this as they onboard.
          </div>
        ) : (
          <div>
            <table className="table-stack w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-left" style={{ color: "var(--text-dim)" }}>
                  <th className="pb-3 font-normal">City</th>
                  <th className="pb-3 font-normal">Students</th>
                  <th className="pb-3 font-normal">Companies</th>
                  <th className="pb-3 font-normal">Tasks</th>
                  <th className="pb-3 font-normal w-1/3">Activity</th>
                </tr>
              </thead>
              <tbody>
                {cities.map((c, i) => {
                  const total = c.students + c.companies + c.tasks;
                  const pct = Math.round((total / maxCityActivity) * 100);
                  return (
                    <tr
                      key={c.city + i}
                      data-testid={`analytics-city-row-${i}`}
                      className="border-t transition-colors duration-200 hover:bg-white/[0.02]"
                      style={{ borderColor: "var(--border)" }}
                    >
                      <td className="py-3">
                        <div className="inline-flex items-center gap-2" style={{ color: "var(--text)" }}>
                          <MapPin size={12} style={{ color: "var(--brand)" }}/>
                          {c.city}
                        </div>
                      </td>
                      <td data-label="Students" className="py-3 font-mono text-xs" style={{ color: "var(--text-muted)" }}><span>{c.students}</span></td>
                      <td data-label="Companies" className="py-3 font-mono text-xs" style={{ color: "var(--text-muted)" }}><span>{c.companies}</span></td>
                      <td data-label="Tasks" className="py-3 font-mono text-xs" style={{ color: "var(--text)" }}><span>{c.tasks}</span></td>
                      <td className="py-3">
                        <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "var(--surface-3)" }}>
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${pct}%`,
                              background: "linear-gradient(90deg, var(--brand), var(--accent))",
                            }}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
